import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, TransitionGroup as _TransitionGroup, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "flex flex-wrap gap-x2 justify-center mb-x2" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = { class: "flex flex-wrap justify-center mb-x4" }
const _hoisted_4 = {
  key: 1,
  class: "border -mt-x5 p-x2 md:p-x3 border-silver"
}
const _hoisted_5 = { key: 2 }
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = {
  key: 0,
  class: "flex items-center justify-center"
}
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "c-magic-line" }
const _hoisted_10 = ["onClick"]
const _hoisted_11 = { key: 1 }

import { BaseProductItem, ClickAndCollectCheckOutDeliverable, ClickAndCollectStoreInventoryResult, DeliveryOptionModel } from '@/b2c/shared/api/server-apis';
import { StoreModel } from '@/types/locationServerApi';
import CheckoutDeliverableItems from '@/b2c/checkout/ui/items/CheckoutDeliverableItems.vue';
import CheckoutDeliverableGoToNext from '@/b2c/checkout/ui/CheckoutDeliverableGoToNext.vue';
import BlockLabelWithNamedArguments from '@/shared/BlockLabelWithNamedArguments.vue';
import { computed, ComputedRef, ref, Ref, unref } from 'vue';
import useStore from '@/shared/stores/useStore';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import useBooleanToggle from '@/shared/useBooleanToggle';
import BasketDeliverableDialog from '@/b2c/basket/ui/BasketDeliverableDialog.vue';
import BasketDeliverableStoreInfo from '@/b2c/basket/ui/BasketDeliverableStoreInfo.vue';
import BasketDeliverableStoreSelection from '@/b2c/basket/ui/BasketDeliverableStoreSelection.vue';
import BasketDeliverableItemsSelection from '@/b2c/basket/ui/BasketDeliverableItemsSelection.vue';
import { DeliverableSelectItem, useDeliverables } from '@/b2c/basket/useDeliverables';
import { useCheckout } from '@/b2c/checkout/useCheckout';
import { useStepReset } from '@/b2c/checkout/useStepReset';
import dictionaryService from '@/core/dictionary/dictionary.service';

enum ClickAndCollectStep {
    ShowStoreInfo = 0,
    ChangeStore = 1,
    ChangeStoreConfirmation = 2,
}

interface DeliveryOptionWithSelectedValueModel extends DeliveryOptionModel {
    selected: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CheckoutClickAndCollectDeliverable',
  props: {
    deliverable: null,
    index: null
  },
  setup(__props: any) {

const props = __props as {
    deliverable: ClickAndCollectCheckOutDeliverable;
    index: number;
}



const labels = useBlockLabels();
const resetStep = useStepReset();
const { isToggled, toggle } = useBooleanToggle();
const { basket, changeClickAndCollectStoreFromCheckOut, getDeliveryOptionsForClickAndCollect, addDeliveryOption, removeDeliveryOption } = useCheckout();
const { getSelectItemsFromItems, getClickAndCollectStatus } = useDeliverables();

const step: Ref<ClickAndCollectStep> = ref(ClickAndCollectStep.ShowStoreInfo);

const isShowStoreInfoStep: ComputedRef<boolean> = computed(() => step.value === ClickAndCollectStep.ShowStoreInfo);
const isChangeStoreStep: ComputedRef<boolean> = computed(() => step.value === ClickAndCollectStep.ChangeStore);
const isChangeStoreConfirmationStep: ComputedRef<boolean> = computed(() => step.value === ClickAndCollectStep.ChangeStoreConfirmation);
const selectedStore: Ref<ClickAndCollectStoreInventoryResult | null> = ref(null);
const clickAndCollectStatus: Ref<(ClickAndCollectStoreInventoryResult | undefined)[] | undefined> = ref([]);
const deliveryOptions = ref<(DeliveryOptionModel|undefined)[]>([]);
const deliveryOptionsWithSelectedValue = computed<DeliveryOptionWithSelectedValueModel[]>(() => {
    return (deliveryOptions.value as DeliveryOptionWithSelectedValueModel[]).map(option => {
        option.selected = !!props.deliverable.deliveryOptions?.find(sku => sku === option.sku) ?? false;
        return option;
    });
});

const store: ComputedRef<StoreModel> = computed(() => {
    return unref<StoreModel>(useStore(props.deliverable?.storeId ?? ''));
});

const heading: ComputedRef<string> = computed(() => {
    if (isChangeStoreStep.value) {
        return labels.clickAndCollectDeliverableChangeStoreSelectStoreHeading;
    } else if (isChangeStoreConfirmationStep.value) {
        return dictionaryService.pluralize(itemsNotAvailableInSelectedStore.value.length, labels.clickAndCollectDeliverableChangeStoreSelectStoreItemsNotAvailableHeading, labels.clickAndCollectDeliverableChangeStoreSelectStoreItemsNotAvailableHeadingPlural);
    } else {
        return store.value.name;
    };
});

const showStoreInfo = (): void => {
    step.value = ClickAndCollectStep.ShowStoreInfo;
    toggle();
};

const items: ComputedRef<DeliverableSelectItem[]> = computed(() => {
    const ids = props?.deliverable?.itemIds ?? [];
    const lineItems = ids.map((id) => {
        return basket.value?.items.find(item => item.id === id) || {} as BaseProductItem;
    });
    return getSelectItemsFromItems(lineItems as BaseProductItem[], i => i.clickAndCollectEnabled);
});

const initChangeStore = async(): Promise<void> => {
    selectedStore.value = null;
    clickAndCollectStatus.value = [];
    step.value = ClickAndCollectStep.ChangeStore;
    toggle();
    const ids = items.value.map(sel => sel.itemId);
    const status = await getClickAndCollectStatus(ids);
    clickAndCollectStatus.value = status?.filter(s => s?.itemIdsNotAvailable.length === 0);
};

const selectStore = (store: ClickAndCollectStoreInventoryResult): void => {
    selectedStore.value = store;
    const hasAllItemsAvailable = selectedStore.value.itemIdsNotAvailable.length < 1;
    if (hasAllItemsAvailable) {
        confirmSelectStore();
    } else {
        step.value = ClickAndCollectStep.ChangeStoreConfirmation;
    }
};

const goToSelectStore = (): void => {
    step.value = ClickAndCollectStep.ChangeStore;
};

const confirmSelectStore = async(): Promise<void> => {
    if (!selectedStore.value) {
        return;
    }
    toggle();
    resetStep();
    await changeClickAndCollectStoreFromCheckOut(selectedStore.value.storeId);
};

const itemsNotAvailableInSelectedStore: ComputedRef<DeliverableSelectItem[]> = computed(() => {
    const itemsNotAvailable = items.value.filter(item => selectedStore.value?.itemIdsNotAvailable.includes(item.itemId) ?? [] as DeliverableSelectItem[]);
    return itemsNotAvailable;
});

const setDeliveryOption = async(): Promise<void> => {
    const response = await getDeliveryOptionsForClickAndCollect();
    deliveryOptions.value = response;
};

setDeliveryOption();

const onDeliveryOptionChange = (option: DeliveryOptionWithSelectedValueModel) => {
    if (option.selected) {
        addDeliveryOption(props.deliverable.id, option.sku);
    } else {
        removeDeliveryOption(props.deliverable.id, option.sku);
    }
};

return (_ctx: any,_cache: any) => {
  const _component_InputCheckbox = _resolveComponent("InputCheckbox")!
  const _component_c_icon = _resolveComponent("c-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(BlockLabelWithNamedArguments, {
      label: __props.deliverable.text,
      class: "mb-x2 text-center"
    }, null, 8, ["label"]),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_Transition, {
        name: "o-anim-fade-300",
        mode: "out-in",
        appear: ""
      }, {
        default: _withCtx(() => [
          (_unref(store).name)
            ? (_openBlock(), _createElementBlock("div", {
                key: __props.deliverable.storeId,
                innerHTML: _ctx.$dictionary.formatNamed(_unref(labels).clickAndCollectDeliverableSelectedStoreInfo, { store: _unref(store).name })
              }, null, 8, _hoisted_2))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createElementVNode("button", {
        type: "button",
        class: "c-magic-line c-magic-line--underline text-dusty-grey",
        onClick: initChangeStore
      }, _toDisplayString(_unref(labels).clickAndCollectChangeStoreLink), 1)
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("button", {
        type: "button",
        class: "c-magic-line c-magic-line--underline text-dusty-grey",
        onClick: showStoreInfo
      }, _toDisplayString(_unref(labels).clickAndCollectShowStoreInfoLink), 1)
    ]),
    (__props.deliverable.id)
      ? (_openBlock(), _createBlock(CheckoutDeliverableItems, {
          key: 0,
          deliverable: __props.deliverable
        }, null, 8, ["deliverable"]))
      : _createCommentVNode("", true),
    (deliveryOptions.value?.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createVNode(_TransitionGroup, {
            name: "o-anim-fade-200",
            mode: "out-in"
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(deliveryOptionsWithSelectedValue), (option) => {
                return (_openBlock(), _createBlock(_component_InputCheckbox, {
                  key: option?.sku,
                  modelValue: option.selected,
                  "onUpdate:modelValue": ($event: any) => ((option.selected) = $event),
                  name: `deliveryOptions-${option.sku}`,
                  class: "mb-x2 pb-0 last:mb-0",
                  label: `${option.description} + ${option.price.amount}`,
                  "show-errors": false,
                  onInput: ($event: any) => (onDeliveryOptionChange(option))
                }, null, 8, ["modelValue", "onUpdate:modelValue", "name", "label", "onInput"]))
              }), 128))
            ]),
            _: 1
          })
        ]))
      : _createCommentVNode("", true),
    _createVNode(CheckoutDeliverableGoToNext, {
      index: __props.index,
      deliverable: __props.deliverable
    }, null, 8, ["index", "deliverable"]),
    _createVNode(BasketDeliverableDialog, {
      "is-open": _unref(isToggled),
      heading: _unref(heading),
      onClose: _unref(toggle)
    }, {
      navigation: _withCtx(() => [
        _createVNode(_Transition, {
          name: "o-anim-fade-300",
          mode: "out-in"
        }, {
          default: _withCtx(() => [
            (_unref(isChangeStoreConfirmationStep))
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "flex flex-nowrap items-center mr-x2",
                    onClick: _withModifiers(goToSelectStore, ["prevent"])
                  }, [
                    _createVNode(_component_c_icon, {
                      name: "icon-arrow-small",
                      class: "rotate-180 w-8 h-8 mt-1 mr-6 xl:h-10 xl:mr-8 xl:-mt-1"
                    }),
                    _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(labels).clickAndCollectDeliverableChangeStoreCancelButton), 1)
                  ], 8, _hoisted_8),
                  _createElementVNode("button", {
                    type: "button",
                    class: "c-button c-button--primary ml-x2",
                    onClick: _withModifiers(confirmSelectStore, ["prevent"])
                  }, _toDisplayString(_unref(labels).clickAndCollectDeliverableChangeStoreConfirmButton), 9, _hoisted_10)
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_11))
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(_Transition, {
          name: "o-anim-fade-300",
          mode: "out-in"
        }, {
          default: _withCtx(() => [
            (_unref(isShowStoreInfoStep))
              ? (_openBlock(), _createBlock(BasketDeliverableStoreInfo, {
                  key: 0,
                  id: __props.deliverable.storeId,
                  class: "mx-auto max-w-600"
                }, null, 8, ["id"]))
              : (_unref(isChangeStoreStep))
                ? (_openBlock(), _createBlock(BasketDeliverableStoreSelection, {
                    key: 1,
                    "stores-status": clickAndCollectStatus.value,
                    "select-label": _unref(labels).clickAndCollectDeliverableChangeStoreSelectStoreLink,
                    "selected-id": __props.deliverable.storeId,
                    "selected-label": _unref(labels).clickAndCollectDeliverableChangeStoreSelectedStoreLabel,
                    class: "min-h-250",
                    onSelect: selectStore
                  }, null, 8, ["stores-status", "select-label", "selected-id", "selected-label"]))
                : (_unref(isChangeStoreConfirmationStep))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      _createElementVNode("div", {
                        class: "text-center mb-20",
                        innerHTML: _ctx.$dictionary.pluralize(_unref(itemsNotAvailableInSelectedStore).length, _unref(labels).clickAndCollectDeliverableChangeStoreItemsNotAvailableDescription, _unref(labels).clickAndCollectDeliverableChangeStoreItemsNotAvailableDescriptionPlural, { store: selectedStore.value?.storeTitle })
                      }, null, 8, _hoisted_6),
                      _createVNode(BasketDeliverableItemsSelection, {
                        items: _unref(itemsNotAvailableInSelectedStore),
                        "read-only": true,
                        class: "mx-auto max-w-500 min-h-150 flex flex-col justify-center"
                      }, null, 8, ["items"])
                    ]))
                  : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["is-open", "heading", "onClose"])
  ]))
}
}

})